import { Component, ViewChild, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { PsvPersonProfile } from 'app/cs/psv/common/model/person/psvPersonProfile';
import {
    HisAuthService, HisSearchResultComponent, HisNavigationService, HisSearch,
    HisSearchResultRow, HisUiService, HisBaseGlobalsService
} from 'hisng';
import { UnitDetailViewListEntry } from './detail-view-list-entry/unit-detail-view-list-entry';
import { PublicationDetailViewListEntry } from './detail-view-list-entry/publication-detail-view-list-entry';
import { ProjectDetailViewListEntry } from './detail-view-list-entry/project-detail-view-list-entry';
import { PatentDetailViewListEntry } from './detail-view-list-entry/patent-detail-view-list-entry';
import { PrizeNominationDetailViewListEntry } from './detail-view-list-entry/prize-nominations-detail-view-list-entry';
import { ActivityDetailViewListEntry } from './detail-view-list-entry/activity-detail-view-list-entry';
import { AbstractDetailViewListEntry } from './detail-view-list-entry/abstract-detail-view-list-entry';
import { DetailViewListUtils } from './detail-view-list-entry/detail-view-list-utils';
import { PaginatorState } from 'primeng/paginator';



@Component({
    selector: 'psv-person-detail-view-search-result',
    templateUrl: './person-detail-view-search-result.component.html',
})
export class PersonDetailViewSearchResultComponent implements OnInit, OnChanges {
    @ViewChild(HisSearchResultComponent, { static: true }) searchResultComponent: HisSearchResultComponent;

    @Input() search: HisSearch;

    @Input() entityId: string;

    @Input() person: PsvPersonProfile;

    public showList = false;
    public currentPage: number;
    public extendableLineShowAll: Map<number, boolean> = new Map<number, boolean>();
    public extendableLineShowAllSecond: Map<number, boolean> = new Map<number, boolean>();
    public hrefPrefix = window.location.href.split('/a/')[0];
    public detailViewListEntries: { [key: string]: AbstractDetailViewListEntry };

    constructor(
        public auth: HisAuthService,
        private hisUiService: HisUiService,
        private navigationService: HisNavigationService,
        private hisBaseGlobalsService: HisBaseGlobalsService
    ) { }


    public initDetailViewListEntries(): { [key: string]: AbstractDetailViewListEntry } {
        return {
            units: new UnitDetailViewListEntry(this.hisBaseGlobalsService, this.hisUiService, this.person, this),
            publications: new PublicationDetailViewListEntry(this.hisBaseGlobalsService, this.hisUiService, this.person, this),
            projects: new ProjectDetailViewListEntry(this.hisBaseGlobalsService, this.hisUiService, this.person, this),
            patents: new PatentDetailViewListEntry(this.hisBaseGlobalsService, this.hisUiService, this.person, this),
            prizeNominations: new PrizeNominationDetailViewListEntry(this.hisBaseGlobalsService, this.hisUiService, this.person, this),
            activities: new ActivityDetailViewListEntry(this.hisBaseGlobalsService, this.hisUiService, this.person, this, this.auth),
        };
    }


    ngOnChanges(changes: SimpleChanges): void {
        if (changes['person']) {
            this.detailViewListEntries = this.initDetailViewListEntries();
        }
    }

    ngOnInit() {
        setTimeout(() => {
            this.showList = true;
        });
    }

    /* leaving page action use onBeforeNavigate set last back url with navigationService */
    public onBeforeNavigate() {
        this.navigationService.setLastBackUrl(window.location.href);
    }

    setShowAll(extendableLineShowAll: Map<number, boolean>, newValue: boolean, index: number, first: number, rowsPerPage: number) {
        let currentPage = (first / rowsPerPage) + 1;
        let totalIndex = currentPage * rowsPerPage + index;
        return extendableLineShowAll.set(totalIndex, newValue);
    }

    isShowAll(extendableLineShowAll: Map<number, boolean>, index: number, first: number, rowsPerPage: number): boolean {
        let currentPage = (first / rowsPerPage) + 1;
        let totalIndex = currentPage * rowsPerPage + index;
        return extendableLineShowAll.get(totalIndex);
    }

    isShowExtendableLine(index: number, first: number, rowsPerPage: number): boolean {
        return this.isShowAll(this.extendableLineShowAll, index, first, rowsPerPage);
    }

    paginate(event: PaginatorState) {
        this.searchResultComponent.onPage(event);
        this.searchResultComponent.loadPage(event.first);
    }

    public getHisObjectWithForeignTextsForAttribute(row: HisSearchResultRow, entryId: string, column: string, attribute: string): any {
        let isPseudonymized = false;
        let pseudonymizedIdx = row.metadata.findIndex(md => md.label === 'isPseudonymized');
        if (pseudonymizedIdx > -1) {
            isPseudonymized = row.values[pseudonymizedIdx];
        }

        let hisObject: any = {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            language: { id: 12, iso_639_1: 'de' },
            _foreigntext: {},
            attribute
        };
        let defLanguageIso = 'de';

        let langIndex: number = row.metadata.findIndex(md => md.label === 'fs.res.common.language');
        if (langIndex >= 0) {
            // Fixes a typedError on Console if split is called on a empty row.
            if (row.values[langIndex]) {
                let objectDefaultLanguage = row.values[langIndex].split(',')[0];
                hisObject.language.id = objectDefaultLanguage.substring(0, objectDefaultLanguage.indexOf(':'));
                defLanguageIso = objectDefaultLanguage.
                    substring(objectDefaultLanguage.indexOf(':') + 1, objectDefaultLanguage.length);
                hisObject.language.iso_639_1 = defLanguageIso;
            }
        }

        let foreigntext: { [key: string]: { [key: string]: string } } = { de: { attribute } };

        let attrLabel = this.detailViewListEntries[entryId].rowLabels[column];
        hisObject[attribute] = DetailViewListUtils.getRowByLabelKey(row, attrLabel);
        foreigntext[defLanguageIso] = {};
        foreigntext[defLanguageIso][attribute] = hisObject[attribute]; //initial gelieferter Wert

        let index = row.metadata.findIndex(md => md.label === 'foreignTexts');
        if (index === -1 || row.values[index] === null || row.values[index] === '') {
            // eslint-disable-next-line no-underscore-dangle
            hisObject._foreigntext = null;
            return hisObject;
        }

        let foreignTitles: string[] = row.values[index].split('|||,');
        let n = foreignTitles.length;
        foreignTitles[n-1] = (foreignTitles[n-1]).substring(0, foreignTitles[n-1].length-3);

        foreignTitles.forEach(record => {
            let recordData: string[] = record.split(':');
            let foreignLang = this.getIso6391(recordData[0].trim());
            let tableName = recordData[1].trim();
            let colName = DetailViewListUtils.camelCase(recordData[2].trim());
            let value = recordData[3];

            //entryIds are in plural names
            let entryObjectName ='';
            if(entryId=== 'activities'){
                entryObjectName ='activity';
            }
            else{
                entryObjectName=entryId.substring(0,entryId.length-1); 
            }            
            if (tableName === entryObjectName) {

                foreigntext[foreignLang] = { ...foreigntext[foreignLang], colName };
                foreigntext[foreignLang][colName] = value;
            }

        });
        // eslint-disable-next-line no-underscore-dangle
        hisObject._foreigntext = foreigntext;
        return hisObject;
    }


    private getIso6391(langId: string): string {
        //let langs = this.hisBaseGlobalsService.getSupportedLanguages();
        //TODO: service methode schreiben
        // Fixes TypedError on Console
        if (langId === null) {
            return 'de';
        }
        switch (langId) {
            case '5':
                return 'en';
            case '12':
                return 'de';
            case '13':
                return 'la';
            case '14':
                return 'es';
            case '18':
                return 'fr';
            default: return 'de';
        }
    }

    getParameterName(row: HisSearchResultRow): string {
        let typeHiskeyId = DetailViewListUtils.getRowByLabelKey(row, 'fs.res.researchActivity.ResearchActivity.researchActivityTypeValue.parent.hiskeyId');
        switch (parseInt(typeHiskeyId, 10)) {
            // Mitgliedschaften
            case 1031:
                return 'groupName';
        }
        return 'title';
    }

}


